import { useMemo, useState } from "react";
import classNames from "classnames";
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "@/components";
import exportModalStyles from "@/components/export-modal/ExportModal.module.scss";
import { downloadCsvFile } from "@/helpers/csv";
import {
  STATISTICS_EXPORT_METRICS,
  type StatisticsExportMetricKey,
} from "../../_constants/statistics-export.const";
import type { StatisticsOverviewData } from "../../_types/statistics-api.types";
import { buildStatisticsDailyCsv } from "../../_utils/statisticsExportCsv.util";
import styles from "./StatisticsExportDrawer.module.scss";

function formatPeriodSubtitle(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  const fmt = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
  return `${fmt.format(from)} – ${fmt.format(to)}`;
}

function buildExportFilename(fromIso: string, toIso: string): string {
  const day = (iso: string) => iso.slice(0, 10).replace(/[^0-9-]/g, "");
  return `vetty-statistics-daily-${day(fromIso)}_${day(toIso)}.csv`;
}

type StatisticsExportDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  overview: StatisticsOverviewData | null;
  loading: boolean;
};

export const StatisticsExportDrawer = ({
  isOpen,
  onClose,
  overview,
  loading,
}: StatisticsExportDrawerProps) => {
  const [selectedKeys, setSelectedKeys] = useState<
    StatisticsExportMetricKey[]
  >(
    () =>
      STATISTICS_EXPORT_METRICS.filter((m) => m.defaultSelected).map(
        (m) => m.key,
      ),
  );
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportOptions = useMemo(
    () =>
      STATISTICS_EXPORT_METRICS.map((m) => ({
        key: m.key,
        label: m.label,
      })),
    [],
  );

  const periodSubtitle =
    overview != null
      ? formatPeriodSubtitle(overview.from, overview.to)
      : null;

  const handleClose = () => {
    if (exporting) return;
    setExportError(null);
    onClose();
  };

  const handleToggle = (key: string, checked: boolean) => {
    setExportError(null);
    const typed = key as StatisticsExportMetricKey;
    setSelectedKeys((current) => {
      if (checked) {
        return current.includes(typed) ? current : [...current, typed];
      }
      return current.filter((k) => k !== typed);
    });
  };

  const handleSelectAll = () => {
    setExportError(null);
    setSelectedKeys(STATISTICS_EXPORT_METRICS.map((m) => m.key));
  };

  const handleClear = () => {
    setExportError(null);
    setSelectedKeys([]);
  };

  const handleExport = () => {
    if (!selectedKeys.length) {
      setExportError("Select at least one metric to export.");
      return;
    }
    if (!overview?.daily?.length) {
      setExportError("No daily rows for this period.");
      return;
    }

    setExporting(true);
    setExportError(null);
    try {
      const csv = buildStatisticsDailyCsv(overview.daily, selectedKeys);
      downloadCsvFile(
        buildExportFilename(overview.from, overview.to),
        csv,
      );
      onClose();
    } catch {
      setExportError("Could not create the CSV file. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const selectedCount = selectedKeys.length;
  const canExport =
    !loading && overview != null && (overview.daily?.length ?? 0) > 0;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Export statistics">
      <DrawerHeader
        title="Export data"
        subtitle={periodSubtitle ?? undefined}
        onClose={handleClose}
        closeDisabled={exporting}
      />
      <DrawerBody>
        <div className={styles["stack"]}>
          <p
            className={classNames(
              exportModalStyles["comp__description"],
              styles["descriptionFirst"],
            )}
          >
            Choose which metrics to include. Each row is one day in the calendar
            range above; values are the same daily series as on the dashboard.
          </p>

          <div className={exportModalStyles["comp__toolbar"]}>
            <div className={exportModalStyles["comp__selection-count"]}>
              {selectedCount} of {exportOptions.length} metrics selected
            </div>

            <div className={exportModalStyles["comp__toolbar-actions"]}>
              <Button
                size="small"
                variant="secondary"
                type="button"
                onClick={handleSelectAll}
                disabled={exporting}
              >
                Select all
              </Button>
              <Button
                size="small"
                variant="secondary"
                type="button"
                onClick={handleClear}
                disabled={exporting}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className={exportModalStyles["comp__options"]}>
            {exportOptions.map((option) => (
              <div
                key={option.key}
                className={exportModalStyles["comp__option"]}
              >
                <Checkbox
                  id={`statistics-export-${option.key}`}
                  variant="primary"
                  checked={selectedKeys.includes(
                    option.key as StatisticsExportMetricKey,
                  )}
                  disabled={exporting}
                  onChange={(checked) => handleToggle(option.key, checked)}
                >
                  <div className={exportModalStyles["comp__option-content"]}>
                    <span className={exportModalStyles["comp__option-label"]}>
                      {option.label}
                    </span>
                  </div>
                </Checkbox>
              </div>
            ))}
          </div>

          {!canExport && !loading ? (
            <div className={exportModalStyles["comp__error"]} role="status">
              Load statistics for a date range before exporting.
            </div>
          ) : null}

          {exportError ? (
            <div className={exportModalStyles["comp__error"]} role="alert">
              {exportError}
            </div>
          ) : null}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={handleClose}
          disabled={exporting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleExport}
          loading={exporting}
          disabled={
            !selectedCount || !canExport || exporting || loading
          }
        >
          Export CSV
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
